import { intercomService } from "@services/intercomService";
import {
  useWSModal,
  WSActions,
  WSForm,
  WSInputText,
  WSList
} from "@wingspanhq/fe-component-library";
import { VerificationLevel } from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";
import {
  queriesComplianceEntities,
  queriesOnboarding,
  queriesPersons
} from "@query";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "@components/ErrorAlert";
import { validatorName } from "../../../../../shared/validators/validatorName";
import { FormAddPassword } from "../../../../../modules/Settings/routes/RouteAccount/components/FormAddPassword";
import { FormChangePassword } from "../../../../../modules/Settings/routes/RouteAccount/components/FormChangePassword";

const useQueryPrimaryEntity = () => {
  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});
  return queryComplianceEntities.data?.data?.find(entity => !entity.subject);
};

type NameFormProps = { onClose: (e: any) => any };

const FormPreferredNameEdit: React.FC<NameFormProps> = props => {
  const primaryEntity = useQueryPrimaryEntity();
  const [mutate, meta] =
    queriesComplianceEntities.updateComplianceEntity.useMutation({
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase
      ],
      onSuccess: props.onClose
    });

  return (
    <WSForm<{ preferredName: string }>
      defaultValues={{ preferredName: primaryEntity?.preferredName || "" }}
      onSubmit={data => {
        if (!primaryEntity?.id) {
          return;
        }
        mutate({
          complianceEntityId: primaryEntity.id,
          complianceEntityUpdate: { preferredName: data.preferredName }
        });
      }}
      validationSchema={Yup.object({ preferredName: validatorName })}
    >
      <WSList gap="L">
        <WSForm.Field
          name="preferredName"
          label="Preferred name"
          component={WSInputText}
        />
        <ErrorAlert context="Other" error={meta.error} />
        <WSActions
          buttons={[
            { label: "Update", loading: meta.isLoading },
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose(undefined)
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};

// Once the tax identity is verified, a legal-name change is a compliance
// event, not a self-service edit — the same reason `RouteChange` exists for
// the main app's identity-change flow. This portal has no equivalent wizard,
// so it keeps v1's behavior: route the request to support instead of writing
// directly.
const useIsTaxVerified = () => {
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { select: status => status.verifications }
  );
  return queryVerifications.data?.tax === VerificationLevel.VERIFIED;
};

const FormLegalFirstNameEdit: React.FC<NameFormProps> = props => {
  const isVerified = useIsTaxVerified();
  const primaryEntity = useQueryPrimaryEntity();
  const currentFirstName = primaryEntity?.individualLegalName?.givenName || "";

  const [mutate, meta] =
    queriesComplianceEntities.updateComplianceEntity.useMutation({
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase
      ],
      onSuccess: props.onClose
    });

  return (
    <WSForm<{ firstName: string }>
      defaultValues={{ firstName: currentFirstName }}
      onSubmit={data => {
        if (currentFirstName && isVerified) {
          intercomService.openWithMessage(
            `Hi there! I want to update my legal first name to "${data.firstName}".\n\n`
          );
          props.onClose(undefined);
          return;
        }

        if (!primaryEntity?.id) {
          return;
        }

        mutate({
          complianceEntityId: primaryEntity.id,
          complianceEntityUpdate: {
            individualLegalName: {
              ...primaryEntity.individualLegalName,
              givenName: data.firstName
            }
          }
        });
      }}
      validationSchema={Yup.object({
        firstName: validatorName.required("This field is required")
      })}
    >
      <WSList gap="L">
        <WSForm.Field
          name="firstName"
          label="Legal first name"
          component={WSInputText}
        />
        <ErrorAlert context="Other" error={meta.error} />
        <WSActions
          buttons={[
            {
              label:
                currentFirstName && isVerified
                  ? "Send request to customer support"
                  : "Update",
              loading: meta.isLoading
            },
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose(undefined)
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};

const FormLegalLastNameEdit: React.FC<NameFormProps> = props => {
  const isVerified = useIsTaxVerified();
  const primaryEntity = useQueryPrimaryEntity();
  const currentLastName = primaryEntity?.individualLegalName?.familyName || "";

  const [mutate, meta] =
    queriesComplianceEntities.updateComplianceEntity.useMutation({
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase
      ],
      onSuccess: props.onClose
    });

  return (
    <WSForm<{ lastName: string }>
      defaultValues={{ lastName: currentLastName }}
      onSubmit={data => {
        if (currentLastName && isVerified) {
          intercomService.openWithMessage(
            `Hi there! I want to update my legal last name to "${data.lastName}".\n\n`
          );
          props.onClose(undefined);
          return;
        }

        if (!primaryEntity?.id) {
          return;
        }

        mutate({
          complianceEntityId: primaryEntity.id,
          complianceEntityUpdate: {
            individualLegalName: {
              ...primaryEntity.individualLegalName,
              familyName: data.lastName
            }
          }
        });
      }}
      validationSchema={Yup.object({
        lastName: validatorName.required("This field is required")
      })}
    >
      <WSList gap="L">
        <WSForm.Field
          name="lastName"
          label="Legal last name"
          component={WSInputText}
        />
        <ErrorAlert context="Other" error={meta.error} />
        <WSActions
          buttons={[
            {
              label:
                currentLastName && isVerified
                  ? "Send request to customer support"
                  : "Update",
              loading: meta.isLoading
            },
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose(undefined)
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};

export const useModalPreferredNameEdit = () =>
  useWSModal(FormPreferredNameEdit, {
    title: "Edit preferred name",
    size: "S"
  });

export const useModalFirstNameEdit = () =>
  useWSModal(FormLegalFirstNameEdit, {
    title: "Edit legal first name",
    size: "S"
  });

export const useModalLastNameEdit = () =>
  useWSModal(FormLegalLastNameEdit, {
    title: "Edit legal last name",
    size: "S"
  });

export const useModalUserPasswordEdit = () => {
  const { personId } = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({ personId });
  const modalChangePassword = useWSModal(FormChangePassword, {
    title: "Change password",
    size: "S"
  });
  const modalAddPassword = useWSModal(FormAddPassword, {
    title: "Add password",
    size: "S"
  });

  return {
    open: () => {
      if (queryPerson.data?.hasCredentialsConfigured) {
        modalChangePassword.open();
      } else {
        modalAddPassword.open();
      }
    }
  };
};
